import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Chip, PageHeader } from "@/components/ui-bits";
import {
    Upload,
    Sparkles,
    FileText,
    Trash2,
    Loader2,
    Send,
    ClipboardCopy,
    Save,
    Paperclip,
    ArrowLeft,
    Wand2,
    Plus,
    Check,
    Download,
    Mail as MailIcon,
    RotateCw,
    X,
    Zap,
    Eye,
    Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { RoundSummary } from "@/components/outcomes";
import DevelopmentTab from "@/pages/DevelopmentTab";
import { RichEditor, htmlToPlainText } from "@/components/RichEditor";

// Word/char counts for the rich cover letter (approximate — strips tags first).
function countStats(html) {
    const text = htmlToPlainText(html);
    return {
        chars: text.length,
        words: text.trim().split(/\s+/).filter(Boolean).length,
    };
}

// Backend HTTPException.detail can be a string ("Send failed: ...") or an object
// (e.g. {"code": "attachment_lost", "filename": "...", "detail": "..."}). Normalize
// both into a human-readable message we can show in toasts and inline banners.
function formatApiError(err, fallback = "Something went wrong") {
    const d = err?.response?.data?.detail;
    if (!d) return err?.message || fallback;
    if (typeof d === "string") return d;
    if (typeof d === "object") {
        if (d.code === "attachment_lost") {
            return `Attachment "${d.filename}" is no longer available. Re-upload it from the Manuscript tab and try again.`;
        }
        if (d.code === "file_lost") {
            return `Source file for "${d.filename || "this version"}" is no longer available. Re-upload it from the Development tab.`;
        }
        return d.detail || d.message || JSON.stringify(d);
    }
    return String(d);
}

const TABS = [
    { key: "manuscript", label: "Manuscript" },
    { key: "development", label: "Development" },
    { key: "suggestions", label: "Suggestions" },
    { key: "list", label: "Submission List" },
    { key: "letter", label: "Cover Letter" },
    { key: "emails", label: "Emails" },
];

export default function ProjectDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [project, setProject] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [tab, setTab] = useState("manuscript");
    const [saving, setSaving] = useState(false);

    // Analysis state
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null); // { manuscript_summary, suggestions }
    const [checked, setChecked] = useState({}); // editor_id -> bool

    // Submission list state (from server)
    const [subList, setSubList] = useState(null);

    // Round summary
    const [summary, setSummary] = useState(null);

    // Emails state
    const [coverLetter, setCoverLetter] = useState("");
    const [signoff, setSignoff] = useState("Best,");
    const [senderName, setSenderName] = useState("");
    const [generating, setGenerating] = useState(false);

    const fetchProject = async () => {
        try {
            const p = await api.get(`/projects/${id}`).then((r) => r.data);
            setProject(p);
            setCoverLetter(p.cover_letter || "");
        } catch {
            toast.error("Project not found");
            nav("/projects");
        }
    };

    const fetchSubmissionList = async () => {
        try {
            const s = await api.get(`/projects/${id}/submissions`).then((r) => r.data);
            setSubList(s);
        } catch (e) {
            console.error("Failed to load submission list:", e);
        }
    };

    const fetchSummary = async () => {
        try {
            const s = await api.get(`/projects/${id}/rounds/summary`).then((r) => r.data);
            setSummary(s);
        } catch (e) {
            console.error("Failed to load rounds summary:", e);
        }
    };

    const fetchSettings = async () => {
        try {
            const s = await api.get("/settings").then((r) => r.data);
            setSenderName(s.sender_name || "");
            if (s.default_signoff) setSignoff(s.default_signoff);
        } catch (e) {
            console.error("Failed to load settings:", e);
        }
    };

    useEffect(() => {
        fetchProject();
        fetchSubmissionList();
        fetchSettings();
        fetchSummary();
        api.get("/projects/statuses").then((r) => setStatuses(r.data)).catch(() => {});
        // Listen for the "mark as sent" child event → re-fetch the submissions
        // so the UI flips to the Sent pill.
        const onRefresh = () => {
            fetchSubmissionList();
            fetchSummary();
        };
        window.addEventListener("refresh-submissions", onRefresh);
        return () => window.removeEventListener("refresh-submissions", onRefresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (!project) {
        return <div className="pulse-mono font-mono text-sm text-muted-foreground">Loading project…</div>;
    }

    // ------- Actions -------
    const saveMeta = async (patch) => {
        setSaving(true);
        try {
            const res = await api.patch(`/projects/${id}`, patch);
            setProject(res.data);
        } finally {
            setSaving(false);
        }
    };

    const analyze = async () => {
        setAnalyzing(true);
        setAnalysis(null);
        try {
            // 1. Kick off the background job (returns quickly)
            const kick = await api
                .post(`/projects/${id}/analyze?top_k=15`)
                .then((r) => r.data);
            const jobId = kick.job_id;
            if (!jobId) throw new Error("Backend did not return a job id");

            // 2. Poll for status. Analysis on ~600 editors takes 2-4 min.
            //    Poll every 3 s; give up after 8 min (safety net).
            const started = Date.now();
            const POLL_MS = 3000;
            const TIMEOUT_MS = 8 * 60 * 1000;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (Date.now() - started > TIMEOUT_MS) {
                    throw new Error("Analysis timed out (>8 min). Please try again.");
                }
                await new Promise((r) => setTimeout(r, POLL_MS));
                const status = await api
                    .get(`/projects/${id}/analyze/${jobId}`)
                    .then((r) => r.data)
                    .catch((e) => ({ status: "failed", error: e?.message || "Network error" }));

                if (status.status === "done") {
                    const res = status.result;
                    setAnalysis(res);
                    const c = {};
                    (res.suggestions || []).slice(0, 8).forEach((s) => (c[s.editor_id] = true));
                    setChecked(c);
                    setTab("suggestions");
                    toast.success(`Analyzed. ${(res.suggestions || []).length} editors ranked.`);
                    return;
                }
                if (status.status === "failed") {
                    throw new Error(status.error || "Analysis failed");
                }
                // else pending | running → keep polling
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Analysis failed");
        } finally {
            setAnalyzing(false);
        }
    };

    const addSelectedToList = async () => {
        const ids = Object.entries(checked).filter(([_, v]) => v).map(([k]) => k);
        if (!ids.length) return toast.error("Select at least one editor");
        try {
            const res = await api.post(`/projects/${id}/submissions/add`, { editor_ids: ids });
            setSubList(res.data);
            toast.success(`${ids.length} editor(s) added to your list`);
            setTab("list");
        } catch (e) {
            toast.error("Failed to add");
        }
    };

    const removeFromList = async (editor_id) => {
        try {
            const res = await api.post(`/projects/${id}/submissions/remove`, { editor_ids: [editor_id] });
            setSubList(res.data);
        } catch (e) {
            console.error("Failed to remove editor from submission list:", e);
        }
    };

    // Manual add flow — used by AddEditorDialog on both the Submission List tab
    // and the Suggestions tab. Accepts an array of existing editor ids.
    const addEditorIdsToList = async (editorIds) => {
        if (!editorIds.length) return null;
        const res = await api.post(`/projects/${id}/submissions/add`, { editor_ids: editorIds });
        setSubList(res.data);
        return res.data;
    };

    // Manual add — creates a brand-new editor then adds them to the submission list.
    const createEditorAndAddToList = async (editorPayload) => {
        const newEditor = await api.post(`/editors`, editorPayload).then((r) => r.data);
        await addEditorIdsToList([newEditor.id]);
        return newEditor;
    };

    const generateAll = async () => {
        if (!subList || !subList.entries.length) return toast.error("Add editors to your list first");
        if (!coverLetter.trim()) return toast.error("Write a cover letter first");
        setGenerating(true);
        try {
            const eids = subList.entries.map((e) => e.editor_id);
            await api.post(`/emails/generate`, {
                project_id: id,
                editor_ids: eids,
                cover_letter: coverLetter,
                sender_name: senderName,
                sender_signoff: signoff,
            });
            toast.success("Drafts generated");
            await fetchSubmissionList();
            setTab("emails");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    const saveDraft = async (editor_id, subject, body) => {
        try {
            const res = await api.post(`/projects/${id}/submissions/update-draft`, {
                editor_id, subject, body,
            });
            setSubList(res.data);
        } catch (e) {
            toast.error("Failed to save draft");
        }
    };

    const sendOne = async (editor_id, subject, body, attachmentFilenames) => {
        try {
            const res = await api.post(`/emails/send`, {
                project_id: id,
                editor_id,
                subject,
                body,
                attachment_ids: attachmentFilenames,
            }).then((r) => r.data);
            toast.success("Sent · " + (res.resend_id || "ok"));
            await fetchSubmissionList();
            fetchSummary();
        } catch (e) {
            const msg = formatApiError(e, "Send failed");
            // Also print the full detail to the console so the user can copy it if
            // needed even if the toast disappears.
            console.error("Email send failed:", e?.response?.data || e);
            toast.error(msg);
            // Refresh so the failed status + inline error show up on the card.
            fetchSubmissionList();
        }
    };

    return (
        <div>
            <Link
                to="/projects"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
                data-testid="project-back"
            >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All projects
            </Link>

            <PageHeader
                eyebrow={[project.status, project.category, project.age_range, `Round ${project.current_round || 1}`]
                    .filter(Boolean)
                    .join(" · ") || "Project"}
                title={<InlineEditableTitle value={project.title} onSave={(t) => saveMeta({ title: t })} />}
                description={project.author ? `by ${project.author}` : undefined}
                testId="project-header"
                right={
                    <>
                        <button
                            className="btn-ghost"
                            onClick={async () => {
                                const cur = project.current_round || 1;
                                if (!confirm(`Start Round ${cur + 1}? This clears the current draft/sent statuses on your submission list so you can build a fresh round. Previous sends stay in History.`)) return;
                                try {
                                    await api.post(`/projects/${id}/rounds/next`);
                                    toast.success(`Round ${cur + 1} started`);
                                    fetchProject();
                                    fetchSubmissionList();
                                    fetchSummary();
                                } catch (e) {
                                    toast.error("Failed to start next round");
                                }
                            }}
                            data-testid="project-next-round"
                            title="Start a new round of submissions"
                        >
                            <RotateCw className="w-4 h-4" strokeWidth={1.5} />
                            Start Round {(project.current_round || 1) + 1}
                        </button>
                        <button
                            className="btn-ghost"
                            onClick={async () => {
                                if (!confirm("Delete this project?")) return;
                                await api.delete(`/projects/${id}`);
                                nav("/projects");
                            }}
                            data-testid="project-delete"
                        >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                        </button>
                        <button
                            className="btn-primary disabled:opacity-60"
                            onClick={analyze}
                            disabled={analyzing}
                            data-testid="project-analyze"
                        >
                            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                            {analyzing ? "Analyzing…" : "Analyze & Suggest"}
                        </button>
                    </>
                }
            />

            {/* Round summary strip */}
            {summary && summary.rounds && summary.rounds.length > 0 && (
                <div className="mb-8 -mt-4">
                    <RoundSummary rounds={summary.rounds} currentRound={summary.current_round} />
                </div>
            )}

            {/* Tabs */}
            <nav className="flex gap-6 border-b border-border mb-8 -mt-2" data-testid="project-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`tab-${t.key}`}
                        className={
                            "py-3 text-sm font-mono uppercase tracking-widest relative transition-colors " +
                            (tab === t.key ? "text-foreground" : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        {t.label}
                        <span
                            className={
                                "absolute -bottom-px left-0 right-0 h-[2px] " +
                                (tab === t.key ? "bg-[#8C2D19]" : "bg-transparent")
                            }
                        />
                    </button>
                ))}
            </nav>

            {tab === "manuscript" && (
                <ManuscriptTab project={project} onSaveMeta={saveMeta} onRefresh={fetchProject} saving={saving} />
            )}
            {tab === "development" && (
                <DevelopmentTab
                    project={project}
                    statuses={statuses}
                    onProjectChange={(p) => setProject(p)}
                />
            )}
            {tab === "suggestions" && (
                <SuggestionsTab
                    analyzing={analyzing}
                    analysis={analysis}
                    checked={checked}
                    setChecked={setChecked}
                    onAdd={addSelectedToList}
                    onAnalyze={analyze}
                    project={project}
                    subList={subList}
                    onAddEditorIds={addEditorIdsToList}
                    onCreateEditor={createEditorAndAddToList}
                />
            )}
            {tab === "list" && (
                <ListTab
                    subList={subList}
                    onRemove={removeFromList}
                    onGoTo={setTab}
                    projectId={id}
                    projectTitle={project.title}
                    project={project}
                    onAddEditorIds={addEditorIdsToList}
                    onCreateEditor={createEditorAndAddToList}
                />
            )}
            {tab === "letter" && (
                <LetterTab
                    coverLetter={coverLetter}
                    setCoverLetter={setCoverLetter}
                    signoff={signoff}
                    setSignoff={setSignoff}
                    senderName={senderName}
                    setSenderName={setSenderName}
                    onGenerate={generateAll}
                    generating={generating}
                    listCount={subList?.entries?.length || 0}
                    onSave={() => saveMeta({ cover_letter: coverLetter })}
                />
            )}
            {tab === "emails" && (
                <EmailsTab
                    project={project}
                    subList={subList}
                    onSaveDraft={saveDraft}
                    onSend={sendOne}
                    onRefresh={fetchSubmissionList}
                />
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Manuscript tab
// -----------------------------------------------------------------------------
function ManuscriptTab({ project, onSaveMeta, onRefresh, saving }) {
    const [meta, setMeta] = useState(project);
    const [extracting, setExtracting] = useState(false);
    const [lookupHint, setLookupHint] = useState(""); // "Filled from client directory" etc.
    // Development-tab drafts are the AI reader's source of truth now. We track
    // the latest version here so the "Auto-fill" button can enable/disable
    // and the "AI Summary" panel can point to the right draft.
    const [latestVersion, setLatestVersion] = useState(null);
    useEffect(() => setMeta(project), [project.id, project.title]); // eslint-disable-line

    // Fetch the latest Development version whenever the project changes. Cheap
    // (single doc) and gives us reactive state for the auto-fill button.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const rows = await api.get(`/projects/${project.id}/versions`).then((r) => r.data);
                if (cancelled) return;
                setLatestVersion(Array.isArray(rows) && rows.length ? rows[0] : null);
            } catch (e) {
                if (!cancelled) setLatestVersion(null);
            }
        })();
        return () => { cancelled = true; };
    }, [project.id, project.updated_at]);

    const legacyManuscriptFiles = project.manuscript_files || [];
    const hasAISource = !!latestVersion || legacyManuscriptFiles.length > 0;

    const set = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

    // Debounced client-directory lookup: when the user types an author name and
    // the email field is empty, try to auto-fill it from the /api/clients directory.
    // We track the last name we auto-filled from so we can show a persistent hint
    // and avoid re-looking-up on every effect run.
    const [autoFilledFor, setAutoFilledFor] = useState(null);
    useEffect(() => {
        const name = (meta.author || "").trim();
        // Already auto-filled for this exact name → keep the hint, don't re-fetch.
        if (autoFilledFor && autoFilledFor === name) return;
        // If email is present but we didn't fill it (user typed), clear hint & bail.
        if ((meta.author_email || "").trim()) {
            if (autoFilledFor !== name) setLookupHint("");
            return;
        }
        if (!name) {
            setLookupHint("");
            setAutoFilledFor(null);
            return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const res = await api.get(`/clients/lookup?name=${encodeURIComponent(name)}`);
                if (cancelled) return;
                const em = res?.data?.email;
                if (em) {
                    setMeta((m) => (m.author_email ? m : { ...m, author_email: em }));
                    setLookupHint(`Filled from client directory (${res.data.name})`);
                    setAutoFilledFor(name);
                }
            } catch (_e) {
                setLookupHint("");
            }
        }, 500);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [meta.author, meta.author_email, autoFilledFor]);

    const autoFillFromManuscript = async ({ silent = false, onlyEmpty = false } = {}) => {
        if (!hasAISource) {
            if (!silent) toast.error("Upload a draft in the Development tab first");
            return;
        }
        // Manual (explicit) run: warn before overwriting
        if (!silent && !onlyEmpty) {
            const hasContent =
                ["synopsis", "logline", "themes", "comps", "category", "age_range"].some(
                    (k) => (meta[k] || "").trim()
                ) || meta.word_count;
            if (hasContent) {
                const ok = window.confirm(
                    "This will replace the current metadata (Logline, Synopsis, Word count, Themes, Comps, Category, Age range) with values from the manuscript. Continue?"
                );
                if (!ok) return;
            }
        }
        setExtracting(true);
        try {
            const q = onlyEmpty ? "?only_empty=true" : "";
            const data = await api
                .post(`/projects/${project.id}/extract_metadata${q}`)
                .then((r) => r.data);
            if (!data || Object.keys(data).length === 0) {
                if (!silent) toast.info("Nothing to fill — all fields already have values");
                return;
            }
            setMeta((m) => ({
                ...m,
                ...("logline" in data ? { logline: data.logline } : {}),
                ...("synopsis" in data ? { synopsis: data.synopsis } : {}),
                ...("word_count" in data ? { word_count: data.word_count } : {}),
                ...("themes" in data ? { themes: data.themes } : {}),
                ...("category" in data ? { category: data.category } : {}),
                ...("age_range" in data ? { age_range: data.age_range } : {}),
                ...("comps" in data ? { comps: data.comps } : {}),
            }));
            const filled = Object.keys(data).length;
            if (silent) {
                toast.success(`Claude auto-filled ${filled} field${filled === 1 ? "" : "s"} — review then Save changes`);
            } else {
                toast.success("Metadata filled — review then Save changes");
            }
        } catch (e) {
            if (!silent) toast.error(formatApiError(e, "Extraction failed"));
            else console.warn("Auto-fill failed silently:", e);
        } finally {
            setExtracting(false);
        }
    };

    // After a Development draft is uploaded (v1, v2, ...), if the sidebar has any
    // empty AI-fillable fields, kick off a silent auto-fill. Guarded by the
    // version id so we only fire once per new upload — re-renders won't retrigger.
    const autoFillFiredRef = useRef(new Set());
    useEffect(() => {
        if (!latestVersion) return;
        const sig = `v:${latestVersion.id}`;
        if (autoFillFiredRef.current.has(sig)) return;

        const AI_FIELDS = ["logline", "synopsis", "themes", "comps", "category", "age_range"];
        const anyEmpty =
            AI_FIELDS.some((k) => !((meta[k] || "").trim())) ||
            meta.word_count == null ||
            meta.word_count === 0;
        if (!anyEmpty) {
            autoFillFiredRef.current.add(sig);
            return;
        }
        // Guard against duplicate runs from React StrictMode / re-renders
        autoFillFiredRef.current.add(sig);
        // Fire silently — Claude call is 10-15s so we don't block the UI
        autoFillFromManuscript({ silent: true, onlyEmpty: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestVersion]);

    const upload = async (kind, files) => {
        if (!files?.length) return;
        const fd = new FormData();
        fd.append("kind", kind);
        Array.from(files).forEach((f) => fd.append("files", f));
        try {
            await api.post(`/projects/${project.id}/files`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${files.length} file(s) uploaded`);
            onRefresh();
        } catch (e) {
            toast.error("Upload failed");
        }
    };

    const deleteFile = async (kind, filename) => {
        try {
            await api.delete(`/projects/${project.id}/files/${encodeURIComponent(filename)}?kind=${kind}`);
            onRefresh();
        } catch (e) {
            console.error("Failed to delete file:", e);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
                {/* AI source-of-truth indicator — replaces the old Manuscript
                    file(s) upload zone. Uploads live in the Development tab now. */}
                <div className="paper border border-border rounded-sm p-5" data-testid="ai-source-indicator">
                    <div className="label-mono mb-2">AI reader source</div>
                    {latestVersion ? (
                        <div className="text-sm">
                            Reading from <span className="font-serif text-base">Draft v{latestVersion.version_number}</span>
                            {latestVersion.label ? <> — <em>{latestVersion.label}</em></> : null}
                            . Upload a newer draft in the{" "}
                            <Link to={`/projects/${project.id}?tab=development`} className="underline text-[#8C2D19]">Development</Link>
                            {" "}tab to update.
                        </div>
                    ) : legacyManuscriptFiles.length > 0 ? (
                        <div className="text-sm text-muted-foreground">
                            Reading from legacy manuscript file
                            {legacyManuscriptFiles.length > 1 ? "s" : ""}
                            {" ("}
                            {legacyManuscriptFiles.map((f) => f.filename).join(", ")}
                            {"). "}
                            Upload a draft in the{" "}
                            <Link to={`/projects/${project.id}?tab=development`} className="underline text-[#8C2D19]">Development</Link>
                            {" "}tab to switch over.
                        </div>
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            No manuscript yet. Head to the{" "}
                            <Link to={`/projects/${project.id}?tab=development`} className="underline text-[#8C2D19]">Development</Link>
                            {" "}tab to upload the first draft — the AI reader and email attachments will draw from there.
                        </div>
                    )}
                </div>

                <FileArea
                    label="Attachments to include in emails"
                    hint="Files that will be attached when you send — usually the latest manuscript, plus optional cover art, dummy pages, or samples."
                    kind="attachment"
                    files={project.attachments || []}
                    onUpload={(fs) => upload("attachment", fs)}
                    onDelete={(fn) => deleteFile("attachment", fn)}
                    testIdPrefix="attachment"
                />

                {project.manuscript_summary && (
                    <div className="paper border border-border rounded-sm p-6" data-testid="ms-summary">
                        <div className="label-mono mb-2">AI Summary</div>
                        <p className="font-serif text-lg leading-relaxed">{project.manuscript_summary}</p>
                    </div>
                )}
            </div>

            <aside className="space-y-4" data-testid="ms-metadata">
                <div className="flex items-center justify-between gap-2">
                    <div className="label-mono flex items-center gap-2">
                        Metadata
                        {extracting && (
                            <span
                                className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground"
                                data-testid="meta-autofill-indicator"
                            >
                                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                Auto-filling…
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn-ghost text-xs disabled:opacity-60"
                        onClick={() => autoFillFromManuscript()}
                        disabled={extracting || !hasAISource}
                        title={
                            hasAISource
                                ? "Overwrite metadata with values extracted from the latest draft"
                                : "Upload a draft in the Development tab first"
                        }
                        data-testid="meta-autofill-btn"
                    >
                        {extracting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
                        )}
                        Auto-fill from manuscript
                    </button>
                </div>
                <Meta label="Title">
                    <input className="input-flat" value={meta.title || ""} onChange={(e) => set("title", e.target.value)} />
                </Meta>
                <Meta label="Author">
                    <input className="input-flat" value={meta.author || ""} onChange={(e) => set("author", e.target.value)} />
                </Meta>
                <Meta label="Author email (for Send to author)">
                    <input
                        className="input-flat font-mono"
                        type="email"
                        placeholder="author@example.com"
                        value={meta.author_email || ""}
                        onChange={(e) => {
                            set("author_email", e.target.value);
                            setLookupHint("");
                            setAutoFilledFor(null);
                        }}
                        data-testid="author-email-field"
                    />
                    {lookupHint && (
                        <div className="mt-1 text-xs text-[#1B362F] font-mono" data-testid="author-email-lookup-hint">
                            {lookupHint}
                        </div>
                    )}
                </Meta>
                <Meta label="Category">
                    <select className="input-flat" value={meta.category || ""} onChange={(e) => set("category", e.target.value)}>
                        <option value="">—</option>
                        {["PB", "BB", "ER", "Chapter books", "MG", "YA", "Graphic Novel", "Adult", "Nonfiction"].map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </Meta>
                <Meta label="Age range">
                    <input className="input-flat" value={meta.age_range || ""} onChange={(e) => set("age_range", e.target.value)} />
                </Meta>
                <Meta label="Word count">
                    <input
                        className="input-flat"
                        type="number"
                        value={meta.word_count || ""}
                        onChange={(e) => set("word_count", e.target.value ? parseInt(e.target.value) : null)}
                    />
                </Meta>
                <Meta label="Themes (comma-separated)">
                    <input className="input-flat" value={meta.themes || ""} onChange={(e) => set("themes", e.target.value)} />
                </Meta>
                <Meta label="Comp titles">
                    <input className="input-flat" value={meta.comps || ""} onChange={(e) => set("comps", e.target.value)} />
                </Meta>
                <Meta label="Logline">
                    <textarea className="input-flat min-h-[80px]" value={meta.logline || ""} onChange={(e) => set("logline", e.target.value)} />
                </Meta>
                <Meta label="Synopsis">
                    <textarea className="input-flat min-h-[140px]" value={meta.synopsis || ""} onChange={(e) => set("synopsis", e.target.value)} />
                </Meta>
                <button
                    className="btn-primary w-full justify-center"
                    onClick={() => onSaveMeta(meta)}
                    disabled={saving}
                    data-testid="meta-save"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                    Save changes
                </button>
            </aside>
        </div>
    );
}

function Meta({ label, children }) {
    return (
        <label className="block">
            <span className="label-mono">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}

// -----------------------------------------------------------------------------
// InlineEditableTitle
// Click (or the pencil icon) to turn the H1 into an input. Enter or blur
// saves; Escape reverts. Keeps the same visual scale as the read-only header
// so the layout never jumps.
// -----------------------------------------------------------------------------
function InlineEditableTitle({ value, onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!editing) setDraft(value || "");
    }, [value, editing]);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const commit = async () => {
        const next = draft.trim();
        if (!next || next === (value || "").trim()) {
            setEditing(false);
            setDraft(value || "");
            return;
        }
        setBusy(true);
        try {
            await onSave(next);
            setEditing(false);
        } catch (e) {
            toast.error("Failed to save title");
            setDraft(value || "");
        } finally {
            setBusy(false);
        }
    };

    const cancel = () => {
        setEditing(false);
        setDraft(value || "");
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                className="w-full bg-transparent border-b border-[#8C2D19] outline-none font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-foreground pb-1 disabled:opacity-60"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
                }}
                onBlur={commit}
                disabled={busy}
                aria-label="Edit project title"
                data-testid="project-title-input"
            />
        );
    }

    return (
        <button
            type="button"
            className="group inline-flex items-baseline gap-3 text-left cursor-text hover:opacity-90"
            onClick={() => setEditing(true)}
            title="Click to rename the project"
            data-testid="project-title-edit"
        >
            <span>{value || "Untitled"}</span>
            <Pencil
                className="w-4 h-4 mb-1 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                strokeWidth={1.5}
                aria-hidden
            />
        </button>
    );
}

function FileArea({ label, hint, files, onUpload, onDelete, testIdPrefix }) {
    const inputRef = React.useRef(null);
    const [drag, setDrag] = useState(false);
    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <div>
                    <div className="label-mono">{label}</div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-lg">{hint}</p>
                </div>
                <button
                    className="btn-ghost text-xs"
                    onClick={() => inputRef.current?.click()}
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    <Upload className="w-4 h-4" strokeWidth={1.5} /> Upload
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    multiple
                    onChange={(e) => onUpload(e.target.files)}
                    data-testid={`${testIdPrefix}-file-input`}
                />
            </div>
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDrag(false);
                    onUpload(e.dataTransfer.files);
                }}
                className={
                    "border border-dashed rounded-sm p-4 transition-colors " +
                    (drag ? "bg-secondary border-primary" : "border-border bg-white/40")
                }
                data-testid={`${testIdPrefix}-dropzone`}
            >
                {files.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-4">
                        Drop files here or click Upload.
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {files.map((f, idx) => (
                            <li key={`${f.stored_path || f.filename}-${idx}`} className="flex items-center gap-3 py-2 text-sm">
                                <FileText className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                                <span className="truncate flex-1">{f.filename}</span>
                                <span className="label-mono">{(f.size / 1024).toFixed(0)} KB</span>
                                <button
                                    onClick={() => onDelete(f.filename)}
                                    className="text-muted-foreground hover:text-[#8C2D19]"
                                    title="Remove"
                                    data-testid={`${testIdPrefix}-file-remove-${f.filename}`}
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Suggestions tab
// -----------------------------------------------------------------------------
function SuggestionsTab({ analyzing, analysis, checked, setChecked, onAdd, onAnalyze, project, subList, onAddEditorIds, onCreateEditor }) {
    const [addOpen, setAddOpen] = useState(false);

    if (analyzing) {
        return (
            <div className="paper border border-border rounded-sm p-10 text-center">
                <div className="pulse-mono font-mono text-sm text-muted-foreground">
                    Reviewing manuscript, matching editors…
                </div>
            </div>
        );
    }
    if (!analysis) {
        return (
            <div className="paper border border-border rounded-sm p-10 text-center">
                <div className="label-mono mb-3">Ready</div>
                <h3 className="font-serif text-2xl">Run analysis to see ranked editors</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                    We&apos;ll read the manuscript (and any dummy pages) plus your metadata, then rank the top matches from your editor database with reasoning.
                </p>
                <div className="mt-6 flex gap-2 justify-center">
                    <button className="btn-primary" onClick={onAnalyze} data-testid="analyze-cta">
                        <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Analyze & Suggest
                    </button>
                    <button
                        className="btn-ghost"
                        onClick={() => setAddOpen(true)}
                        data-testid="suggestions-add-editor-btn"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.5} /> Add editor manually
                    </button>
                </div>
                {addOpen && (
                    <AddEditorDialog
                        subList={subList}
                        onClose={() => setAddOpen(false)}
                        onAddEditorIds={onAddEditorIds}
                        onCreateEditor={onCreateEditor}
                    />
                )}
            </div>
        );
    }

    const checkedCount = Object.values(checked).filter(Boolean).length;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4" data-testid="suggestions-list">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="label-mono">Suggested editors · {analysis.suggestions.length}</div>
                    <div className="flex gap-2">
                        <button
                            className="btn-ghost"
                            onClick={() => setAddOpen(true)}
                            data-testid="suggestions-add-editor-btn"
                        >
                            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add editor manually
                        </button>
                        <button
                            className="btn-primary disabled:opacity-60"
                            onClick={onAdd}
                            disabled={!checkedCount}
                            data-testid="add-to-list-btn"
                        >
                            <Plus className="w-4 h-4" strokeWidth={1.5} />
                            Add {checkedCount || ""} to submission list
                        </button>
                    </div>
                </div>
                {analysis.suggestions.map((s) => (
                    <label
                        key={s.editor_id}
                        htmlFor={`sug-${s.editor_id}`}
                        data-testid={`suggestion-${s.editor_id}`}
                        className={
                            "block paper border rounded-sm p-5 cursor-pointer transition-colors " +
                            (checked[s.editor_id] ? "border-[#1B362F]" : "border-border hover:border-foreground/40")
                        }
                    >
                        <div className="flex items-start gap-4">
                            <input
                                id={`sug-${s.editor_id}`}
                                type="checkbox"
                                checked={!!checked[s.editor_id]}
                                onChange={(e) => setChecked({ ...checked, [s.editor_id]: e.target.checked })}
                                className="mt-1"
                                data-testid={`suggestion-check-${s.editor_id}`}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                                    <div className="font-serif text-xl">{s.name}</div>
                                    <div className="text-sm text-muted-foreground">
                                        {[s.imprint, s.house].filter(Boolean).join(" · ")}
                                    </div>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {(s.categories || []).slice(0, 5).map((c) => (
                                        <Chip key={c}>{c}</Chip>
                                    ))}
                                </div>
                                <p className="mt-3 text-sm leading-relaxed">{s.reasoning}</p>
                            </div>
                            <div className="text-right">
                                <div className="label-mono">Match</div>
                                <div className="font-serif text-3xl leading-none mt-1">{s.score}</div>
                            </div>
                        </div>
                    </label>
                ))}
            </div>

            <aside className="space-y-6">
                <div className="paper border border-border rounded-sm p-5" data-testid="ms-detected">
                    <div className="label-mono mb-2">Detected</div>
                    <dl className="text-sm space-y-2">
                        <Detail label="Category" value={analysis.detected_category} />
                        <Detail label="Age range" value={analysis.detected_age_range} />
                        <Detail
                            label="Themes"
                            value={analysis.detected_themes?.join(", ") || "—"}
                        />
                    </dl>
                </div>
                {analysis.manuscript_summary && (
                    <div className="paper border border-border rounded-sm p-5" data-testid="ms-summary-panel">
                        <div className="label-mono mb-2">Summary</div>
                        <p className="text-sm leading-relaxed">{analysis.manuscript_summary}</p>
                    </div>
                )}
            </aside>
            {addOpen && (
                <AddEditorDialog
                    subList={subList}
                    onClose={() => setAddOpen(false)}
                    onAddEditorIds={onAddEditorIds}
                    onCreateEditor={onCreateEditor}
                />
            )}
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-right">{value || "—"}</dd>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Submission List tab
// -----------------------------------------------------------------------------
function ListTab({ subList, onRemove, onGoTo, projectId, projectTitle, project, onAddEditorIds, onCreateEditor }) {
    const entries = subList?.entries || [];
    const [downloading, setDownloading] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [includeNotes, setIncludeNotes] = useState(true);

    const downloadPdf = async () => {
        if (!entries.length) return;
        setDownloading(true);
        try {
            const endpoint = `${API}/projects/${projectId}/submissions.pdf?include_notes=${includeNotes ? "true" : "false"}`;
            const res = await fetch(endpoint, { method: "GET" });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safe = (projectTitle || "submission").replace(/[^A-Za-z0-9._-]+/g, "_");
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `${safe}_submission-list_${stamp}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error(e.message || "Download failed");
        } finally {
            setDownloading(false);
        }
    };

    if (!entries.length) {
        return (
            <div className="paper border border-border rounded-sm p-10 text-center" data-testid="submission-list-empty">
                <div className="label-mono mb-3">Empty</div>
                <h3 className="font-serif text-2xl">Your submission list is empty</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                    Run analysis and add suggested editors, or add editors from your directory (or type in someone new).
                </p>
                <div className="mt-6 flex gap-2 justify-center flex-wrap">
                    <button
                        className="btn-primary"
                        onClick={() => setAddOpen(true)}
                        data-testid="list-add-editor-btn"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.5} /> Add editor
                    </button>
                    <button
                        className="btn-ghost"
                        onClick={() => onGoTo("suggestions")}
                        data-testid="list-goto-suggestions-btn"
                    >
                        <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Or run AI analysis
                    </button>
                </div>
                {addOpen && (
                    <AddEditorDialog
                        subList={subList}
                        onClose={() => setAddOpen(false)}
                        onAddEditorIds={onAddEditorIds}
                        onCreateEditor={onCreateEditor}
                    />
                )}
            </div>
        );
    }
    return (
        <div className="space-y-4" data-testid="submission-list">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="label-mono">Submission list · {entries.length}</div>
                <div className="flex gap-2 flex-wrap items-center">
                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none pr-2 border-r border-border/60" title="Include the per-editor Editor Notes page in the PDF">
                        <input
                            type="checkbox"
                            checked={includeNotes}
                            onChange={(e) => setIncludeNotes(e.target.checked)}
                            data-testid="list-include-notes"
                        />
                        Editor notes
                    </label>
                    <button
                        className="btn-ghost"
                        onClick={() => setAddOpen(true)}
                        data-testid="list-add-editor-btn"
                        title="Pick an editor from your database, or add someone new"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.5} /> Add editor
                    </button>
                    <button
                        className="btn-ghost disabled:opacity-60"
                        onClick={downloadPdf}
                        disabled={downloading}
                        data-testid="download-list-pdf"
                        title="Download a nicely formatted PDF of this submission list"
                    >
                        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
                        Download PDF
                    </button>
                    <button
                        className="btn-ghost"
                        onClick={() => setSendOpen(true)}
                        data-testid="send-list-to-author-btn"
                        title="Email the PDF submission list to the author"
                    >
                        <MailIcon className="w-4 h-4" strokeWidth={1.5} /> Send to author
                    </button>
                    <button className="btn-primary" onClick={() => onGoTo("letter")} data-testid="go-to-letter">
                        <Wand2 className="w-4 h-4" strokeWidth={1.5} /> Write cover letter →
                    </button>
                </div>
            </div>
            <div className="border border-border rounded-sm bg-white/70 divide-y divide-border">
                {entries.map((e) => (
                    <div key={e.editor_id} className="grid grid-cols-[1.6fr_1.6fr_1fr_140px_40px] px-4 py-3 items-center text-sm" data-testid={`list-row-${e.editor_id}`}>
                        <div>
                            <div className="font-medium">{e.editor?.name}</div>
                            <div className="label-mono">{e.editor?.title || "—"}</div>
                        </div>
                        <div className="truncate">{[e.editor?.imprint, e.editor?.house].filter(Boolean).join(" · ")}</div>
                        <div className="truncate font-mono text-xs">{e.editor?.email || "—"}</div>
                        <div>
                            <StatusChip status={e.status} />
                        </div>
                        <button
                            className="text-muted-foreground hover:text-[#8C2D19]"
                            onClick={() => onRemove(e.editor_id)}
                            data-testid={`list-remove-${e.editor_id}`}
                        >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                ))}
            </div>

            {sendOpen && (
                <SendToAuthorDialog
                    project={project}
                    entryCount={entries.length}
                    onClose={() => setSendOpen(false)}
                />
            )}
            {addOpen && (
                <AddEditorDialog
                    subList={subList}
                    onClose={() => setAddOpen(false)}
                    onAddEditorIds={onAddEditorIds}
                    onCreateEditor={onCreateEditor}
                />
            )}
        </div>
    );
}

function SendToAuthorDialog({ project, entryCount, onClose }) {
    const [toName, setToName] = useState(project?.author || "");
    const [toEmail, setToEmail] = useState(project?.author_email || "");
    const [subject, setSubject] = useState(`Submission list — ${project?.title || ""}`);
    const [message, setMessage] = useState("");
    const [includeNotes, setIncludeNotes] = useState(true);
    const [busy, setBusy] = useState(false);

    const send = async () => {
        if (!toEmail.trim() || !toEmail.includes("@")) {
            return toast.error("Enter a valid author email");
        }
        setBusy(true);
        try {
            const res = await api.post(`/projects/${project.id}/submissions/email-to-author`, {
                to_email: toEmail.trim(),
                to_name: toName.trim(),
                subject: subject.trim(),
                message: message,
                include_notes: includeNotes,
            }).then((r) => r.data);
            toast.success(`Sent to ${res.to}`);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="send-to-author-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-lg p-8 bg-background">
                <div className="label-mono mb-2">Send to Author</div>
                <h3 className="font-serif text-3xl mb-1">Email the submission list</h3>
                <p className="text-sm text-muted-foreground mb-6">
                    We&apos;ll attach a PDF of the {entryCount}-editor list, addressed from your Resend sender identity.
                </p>

                <div className="space-y-4">
                    <label className="block">
                        <span className="label-mono">Author name</span>
                        <input
                            className="input-flat mt-1"
                            value={toName}
                            onChange={(e) => setToName(e.target.value)}
                            data-testid="author-name-input"
                        />
                    </label>
                    <label className="block">
                        <span className="label-mono">Author email *</span>
                        <input
                            className="input-flat mt-1 font-mono"
                            value={toEmail}
                            onChange={(e) => setToEmail(e.target.value)}
                            placeholder="author@example.com"
                            data-testid="author-email-input"
                        />
                    </label>
                    <label className="block">
                        <span className="label-mono">Subject</span>
                        <input
                            className="input-flat mt-1"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            data-testid="author-subject-input"
                        />
                    </label>
                    <label className="block">
                        <span className="label-mono">Note (optional)</span>
                        <textarea
                            className="input-flat mt-1 min-h-[100px]"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Leave blank for a default note, or write your own message."
                            data-testid="author-message-input"
                        />
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
                        <input
                            type="checkbox"
                            checked={includeNotes}
                            onChange={(e) => setIncludeNotes(e.target.checked)}
                            className="mt-1"
                            data-testid="author-include-notes"
                        />
                        <span className="text-sm">
                            <span className="block">Include per-editor "Editor Notes" page</span>
                            <span className="block text-xs text-muted-foreground">
                                Uncheck for a leaner report — just the front page and the editor table.
                            </span>
                        </span>
                    </label>
                </div>

                <div className="flex gap-2 justify-end mt-6">
                    <button className="btn-ghost" onClick={onClose} data-testid="author-send-cancel">Cancel</button>
                    <button
                        className="btn-primary disabled:opacity-60"
                        onClick={send}
                        disabled={busy}
                        data-testid="author-send-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailIcon className="w-4 h-4" strokeWidth={1.5} />}
                        {busy ? "Sending…" : "Send with PDF"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function StatusChip({ status }) {
    const map = {
        pending: { label: "Pending", tone: "" },
        draft_ready: { label: "Draft ready", tone: "strong" },
        sent: { label: "Sent", tone: "strong" },
        failed: { label: "Failed", tone: "red" },
    };
    const s = map[status] || map.pending;
    return <Chip tone={s.tone}>{s.label}</Chip>;
}

// -----------------------------------------------------------------------------
// Cover Letter tab
// -----------------------------------------------------------------------------
function LetterTab({
    coverLetter, setCoverLetter, signoff, setSignoff, senderName, setSenderName,
    onGenerate, generating, listCount, onSave,
}) {
    const { chars, words } = countStats(coverLetter);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
                <div className="paper border border-border rounded-sm p-8 lg:p-12 max-w-prose mx-auto relative overflow-hidden" data-testid="letter-paper">
                    <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                         style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0, transparent 27px, #1c1917 27px, #1c1917 28px)" }} />
                    <div className="relative">
                        <div className="label-mono mb-4">Cover Letter</div>
                        <RichEditor
                            value={coverLetter}
                            onChange={setCoverLetter}
                            placeholder={"Dear editor,\n\nWrite once — I'll personalize it per editor when I generate individual emails. Include the pitch, comps, why-you're-the-agent, and any bio notes for the author.\n\nBest,"}
                            minHeight={480}
                            dataTestId="cover-letter-editor"
                            editorClassName="text-lg leading-[1.75]"
                        />
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                            <div className="label-mono">{chars} chars · {words} words</div>
                            <button className="btn-ghost text-xs" onClick={onSave} data-testid="cover-letter-save">
                                <Save className="w-4 h-4" strokeWidth={1.5} /> Save draft
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <aside className="space-y-5" data-testid="letter-controls">
                <div className="paper border border-border rounded-sm p-5">
                    <div className="label-mono mb-3">Sender</div>
                    <Meta label="Your name">
                        <input className="input-flat" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                    </Meta>
                    <Meta label="Sign-off">
                        <input className="input-flat" value={signoff} onChange={(e) => setSignoff(e.target.value)} />
                    </Meta>
                </div>

                <div className="paper border border-border rounded-sm p-5">
                    <div className="label-mono mb-2">Ready</div>
                    <div className="font-serif text-xl">{listCount} editor{listCount === 1 ? "" : "s"} on list</div>
                    <p className="text-xs text-muted-foreground mt-2">
                        We&apos;ll produce a personalized email per editor based on this cover letter, referencing their preferences and MSWL notes where relevant.
                    </p>
                    <button
                        className="btn-primary w-full justify-center mt-4 disabled:opacity-60"
                        onClick={onGenerate}
                        disabled={generating || !listCount}
                        data-testid="generate-emails-btn"
                    >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                        {generating ? "Generating…" : "Generate personalized emails"}
                    </button>
                </div>
            </aside>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Emails tab
// -----------------------------------------------------------------------------
function EmailsTab({ project, subList, onSaveDraft, onSend, onRefresh }) {
    const entries = subList?.entries || [];
    if (!entries.length) {
        return (
            <div className="paper border border-border rounded-sm p-10 text-center">
                <h3 className="font-serif text-2xl">No emails yet</h3>
                <p className="text-sm text-muted-foreground mt-2">
                    Add editors to your submission list and generate personalized drafts from the Cover Letter tab.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-8" data-testid="emails-list">
            {entries.map((e) => (
                <EmailCard
                    key={e.editor_id}
                    entry={e}
                    project={project}
                    onSaveDraft={onSaveDraft}
                    onSend={onSend}
                />
            ))}
        </div>
    );
}

function EmailCard({ entry, project, onSaveDraft, onSend }) {
    const [subject, setSubject] = useState(entry.email_subject || "");
    const [body, setBody] = useState(entry.email_body || "");
    const allFiles = useMemo(
        () => [...(project.manuscript_files || []), ...(project.attachments || [])],
        [project]
    );
    const defaultAttach = useMemo(() => (project.attachments || []).map((f) => f.filename), [project]);
    const [attach, setAttach] = useState(defaultAttach);
    const [sending, setSending] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        setSubject(entry.email_subject || "");
        setBody(entry.email_body || "");
    }, [entry.editor_id, entry.email_subject, entry.email_body]);

    const editor = entry.editor || {};
    const [gmailBusy, setGmailBusy] = useState(false);
    const [gmailConnected, setGmailConnected] = useState(false);

    useEffect(() => {
        // Cheap probe so the button label reflects reality
        api.get("/oauth/gmail/status").then((r) => setGmailConnected(!!r.data?.connected)).catch(() => {});
    }, []);

    const createGmailDraft = async () => {
        if (!editor.email || !subject.trim() || !body.trim()) {
            toast.error("Fill in subject + body first");
            return;
        }
        setGmailBusy(true);
        try {
            const res = await api.post("/emails/gmail-draft", {
                project_id: project.id,
                editor_id: entry.editor_id,
                subject,
                body,
                attachment_ids: attach,
            }).then((r) => r.data);
            toast.success("Draft created in Gmail — opening…");
            window.open(res.compose_url, "_blank", "noopener,noreferrer");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "gmail_not_connected") {
                toast.error("Gmail isn't connected yet — connect it in Settings first.");
            } else {
                toast.error(typeof detail === "string" ? detail : "Failed to create Gmail draft");
            }
        } finally {
            setGmailBusy(false);
        }
    };

    const copyEmail = async () => {
        const text = `To: ${editor.email}\nSubject: ${subject}\n\n${htmlToPlainText(body)}`;
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    return (
        <div className="paper border border-border rounded-sm p-6" data-testid={`email-card-${entry.editor_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-mono">To</div>
                    <div className="font-serif text-2xl">{editor.name}</div>
                    <div className="text-sm text-muted-foreground">
                        {[editor.title, editor.imprint, editor.house].filter(Boolean).join(" · ")}
                    </div>
                    <div className="font-mono text-xs mt-1">{editor.email || "— no email on file —"}</div>
                </div>
                <div className="text-right">
                    <StatusChip status={entry.status} />
                    {entry.sent_at && (
                        <div className="label-mono mt-1">
                            Sent {new Date(entry.sent_at).toLocaleString()}
                        </div>
                    )}
                </div>
            </div>

            {entry.status === "failed" && entry.error && (
                <div
                    className="mt-4 border border-[#8C2D19]/40 bg-[#8C2D19]/[0.06] rounded-sm p-4"
                    data-testid={`email-error-${entry.editor_id}`}
                >
                    <div className="flex items-start gap-3">
                        <div className="label-mono text-[#8C2D19] mt-0.5 shrink-0">Last send failed</div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-mono break-words whitespace-pre-wrap">
                                {entry.error}
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                                Fix the issue above (usually the Resend API key or verified sender in{" "}
                                <a href="/settings" className="underline">Settings</a>) and press Send again.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-6 space-y-3">
                <label className="block">
                    <span className="label-mono">Subject</span>
                    <input
                        className="input-flat mt-1"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        data-testid={`email-subject-${entry.editor_id}`}
                    />
                </label>
                <div className="block">
                    <span className="label-mono">Body</span>
                    <RichEditor
                        value={body}
                        onChange={setBody}
                        minHeight={220}
                        dataTestId={`email-body-editor-${entry.editor_id}`}
                        editorClassName="text-base leading-relaxed"
                        className="mt-1"
                    />
                </div>
            </div>

            {allFiles.length > 0 && (
                <div className="mt-4">
                    <div className="label-mono mb-2 flex items-center gap-2">
                        <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} /> Attachments
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="attachment-picker">
                        {allFiles.map((f, idx) => {
                            const on = attach.includes(f.filename);
                            // Warn if two files in this project have the same filename —
                            // this used to silently ship the same file twice and drop the
                            // other one on the floor. Fixed backend-side, but we should
                            // still tell the user so they can rename.
                            const dupCount = allFiles.filter((g) => g.filename === f.filename).length;
                            const isDup = dupCount > 1;
                            return (
                                <button
                                    key={`${f.stored_path || f.filename}-${idx}`}
                                    onClick={() =>
                                        setAttach((a) => (on ? a.filter((x) => x !== f.filename) : [...a, f.filename]))
                                    }
                                    data-testid={`attach-${entry.editor_id}-${f.filename}`}
                                    title={isDup ? `Warning: ${dupCount} project files share this filename. Only one copy will be attached — rename the duplicates or delete unused ones.` : ""}
                                    className={
                                        "px-3 py-1 text-xs font-mono border rounded-sm transition-colors " +
                                        (on
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-white border-border hover:bg-secondary") +
                                        (isDup ? " ring-1 ring-[#8C2D19]/50" : "")
                                    }
                                >
                                    {on && <Check className="w-3 h-3 inline mr-1" strokeWidth={2} />}
                                    {f.filename}
                                    {isDup ? <span className="ml-1 text-[10px] text-[#8C2D19]">·dup</span> : null}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2 justify-end mt-6">
                <button
                    className="btn-ghost"
                    onClick={() => onSaveDraft(entry.editor_id, subject, body)}
                    data-testid={`email-save-${entry.editor_id}`}
                >
                    <Save className="w-4 h-4" strokeWidth={1.5} /> Save draft
                </button>
                <button
                    className="btn-ghost"
                    onClick={copyEmail}
                    data-testid={`email-copy-${entry.editor_id}`}
                >
                    <ClipboardCopy className="w-4 h-4" strokeWidth={1.5} /> Copy
                </button>
                <button
                    className="btn-ghost disabled:opacity-60"
                    onClick={() => setPreviewOpen(true)}
                    disabled={!subject.trim() || !body.trim()}
                    data-testid={`email-preview-${entry.editor_id}`}
                    title="See exactly what the recipient will get"
                >
                    <Eye className="w-4 h-4" strokeWidth={1.5} /> Preview
                </button>
                <button
                    className="btn-ghost disabled:opacity-60"
                    onClick={createGmailDraft}
                    disabled={gmailBusy || !subject.trim() || !body.trim()}
                    data-testid={`email-gmail-draft-${entry.editor_id}`}
                    title={gmailConnected ? "Create a Gmail draft and open it for review" : "Connect Gmail in Settings to enable"}
                >
                    {gmailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailIcon className="w-4 h-4" strokeWidth={1.5} />}
                    {gmailConnected ? "Open in Gmail" : "Open in Gmail…"}
                </button>
                {entry.status === "sent" ? (
                    <span
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[#1B362F]/30 bg-[#1B362F]/5 text-[#1B362F] rounded-sm font-mono"
                        data-testid={`email-sent-badge-${entry.editor_id}`}
                        title={entry.sent_at ? `Sent ${new Date(entry.sent_at).toLocaleString()}` : "Already sent — resend is disabled to prevent duplicates"}
                    >
                        <Check className="w-4 h-4" strokeWidth={1.5} />
                        Sent{entry.sent_at ? ` · ${new Date(entry.sent_at).toLocaleDateString()}` : ""}
                    </span>
                ) : (
                    <>
                        <button
                            className="btn-ghost disabled:opacity-60"
                            onClick={async () => {
                                if (!window.confirm(
                                    `Mark this email to ${editor.name || "editor"} as sent? This will move it into your "Latest sends" history.`
                                )) return;
                                try {
                                    await api.post("/emails/mark-sent", {
                                        project_id: project.id,
                                        editor_id: entry.editor_id,
                                        subject,
                                        body,
                                    });
                                    toast.success("Marked as sent");
                                    // Force a refresh so the UI flips to the Sent pill
                                    if (typeof onSaveDraft === "function") {
                                        // onSaveDraft triggers a fetch of the submission list in the parent
                                        // — reuse that plumbing without saving a draft:
                                    }
                                    window.dispatchEvent(new CustomEvent("refresh-submissions"));
                                } catch (e) {
                                    toast.error(formatApiError(e, "Failed to mark sent"));
                                }
                            }}
                            disabled={!subject.trim() || !body.trim()}
                            data-testid={`email-mark-sent-${entry.editor_id}`}
                            title="I already sent this from Gmail — record it as sent"
                        >
                            <Check className="w-4 h-4" strokeWidth={1.5} /> Mark as sent
                        </button>
                        <button
                            className="btn-primary disabled:opacity-60"
                            disabled={sending || !editor.email || !subject.trim() || !body.trim()}
                            onClick={async () => {
                                setSending(true);
                                try {
                                    await onSend(entry.editor_id, subject, body, attach);
                                } finally {
                                    setSending(false);
                                }
                            }}
                            data-testid={`email-send-${entry.editor_id}`}
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                            {sending ? "Sending…" : entry.status === "failed" ? "Retry send" : "Send"}
                        </button>
                    </>
                )}
            </div>
            {previewOpen && (
                <EmailPreviewDialog
                    editor={editor}
                    subject={subject}
                    body={body}
                    attachments={attach}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// EmailPreviewDialog — shows the recipient's-eye view of the outgoing message.
// Body is rendered inside an isolated iframe so any signature/CSS in the HTML
// can't leak out and style the surrounding app.
// -----------------------------------------------------------------------------
function EmailPreviewDialog({ editor, subject, body, attachments, onClose }) {
    const [sender, setSender] = useState({ name: "", email: "" });

    useEffect(() => {
        api.get("/settings")
            .then((r) => setSender({ name: r.data?.sender_name || "", email: r.data?.sender_email || "" }))
            .catch(() => {});
    }, []);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Build the iframe HTML — wrap the body in the same email-safe container the
    // backend uses (services/email_sender.py::_wrap_html) so the preview matches
    // what recipients actually see (12pt sans-serif, double-spaced paragraphs).
    const iframeSrcDoc = useMemo(() => {
        // Mirror the backend's paragraph-spacing injection so the preview
        // renders identical to the sent version even for TipTap output where
        // <p> tags have no inline style.
        const spaced = String(body || "").replace(
            /<p(?![^>]*\bstyle=)/gi,
            '<p style="margin:0 0 1em 0;"',
        );
        return `<!doctype html><html><head><meta charset="utf-8">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { padding: 20px 24px; }
  img { max-width: 100%; height: auto; }
</style>
</head><body>
<div style="font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 12pt; line-height: 1.5; color:#1c1917;">
${spaced}
</div>
</body></html>`;
    }, [body]);

    const fromLine = sender.name && sender.email
        ? `${sender.name} <${sender.email}>`
        : sender.email || "(sender not configured)";
    const toLine = editor?.email
        ? (editor.name ? `${editor.name} <${editor.email}>` : editor.email)
        : "(no email on file)";

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            data-testid="email-preview-dialog"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="paper border border-border rounded-sm w-full max-w-3xl bg-background flex flex-col" style={{ maxHeight: "90vh" }}>
                <div className="p-6 border-b border-border">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="label-mono mb-1">Preview</div>
                            <h2 className="font-serif text-2xl leading-tight">Recipient&apos;s view</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Exactly what {editor?.name || "the editor"} will see when they open the message.
                            </p>
                        </div>
                        <button
                            className="btn-ghost text-xs"
                            onClick={onClose}
                            data-testid="email-preview-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>

                {/* Header rows — like a real Gmail conversation */}
                <div className="p-6 border-b border-border space-y-1.5 text-sm">
                    <div className="grid grid-cols-[80px_1fr] gap-3">
                        <span className="label-mono self-center">From</span>
                        <span className="font-mono text-xs" data-testid="email-preview-from">{fromLine}</span>
                    </div>
                    <div className="grid grid-cols-[80px_1fr] gap-3">
                        <span className="label-mono self-center">To</span>
                        <span className="font-mono text-xs" data-testid="email-preview-to">{toLine}</span>
                    </div>
                    <div className="grid grid-cols-[80px_1fr] gap-3">
                        <span className="label-mono self-center">Subject</span>
                        <span className="font-serif text-base leading-snug" data-testid="email-preview-subject">
                            {subject || <em className="text-muted-foreground">(no subject)</em>}
                        </span>
                    </div>
                    {attachments && attachments.length > 0 && (
                        <div className="grid grid-cols-[80px_1fr] gap-3 pt-1">
                            <span className="label-mono self-start">Attachments</span>
                            <ul className="flex flex-wrap gap-1.5" data-testid="email-preview-attachments">
                                {attachments.map((fn) => (
                                    <li
                                        key={fn}
                                        className="inline-flex items-center gap-1 text-xs font-mono border border-border rounded-sm px-2 py-0.5 bg-secondary/40"
                                    >
                                        <Paperclip className="w-3 h-3" strokeWidth={1.5} />
                                        {fn}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Body — rendered inside an isolated iframe */}
                <div className="flex-1 overflow-hidden bg-white">
                    <iframe
                        title="Email preview"
                        srcDoc={iframeSrcDoc}
                        className="w-full h-full min-h-[400px] border-0"
                        sandbox="allow-same-origin allow-popups"
                        data-testid="email-preview-iframe"
                    />
                </div>

                <div className="p-4 border-t border-border flex justify-end">
                    <button className="btn-primary" onClick={onClose} data-testid="email-preview-done">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// AddEditorDialog — pick from database OR add someone new. Used on both the
// Suggestions tab and the Submission List tab.
// -----------------------------------------------------------------------------
function AddEditorDialog({ subList, onClose, onAddEditorIds, onCreateEditor }) {
    const [mode, setMode] = useState("pick"); // "pick" | "new"
    const alreadyOn = useMemo(
        () => new Set((subList?.entries || []).map((e) => e.editor_id)),
        [subList]
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-editor-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-3xl bg-background flex flex-col" style={{ maxHeight: "88vh" }}>
                <div className="p-6 border-b border-border">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="label-mono mb-1">Add to submission list</div>
                            <h3 className="font-serif text-3xl leading-tight">
                                {mode === "pick" ? "Pick from your editor database" : "Add someone new"}
                            </h3>
                        </div>
                        <button
                            className="btn-ghost text-xs"
                            onClick={onClose}
                            data-testid="add-editor-close"
                        >
                            <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                    <div className="mt-4 flex gap-2" data-testid="add-editor-tabs">
                        <TabBtn active={mode === "pick"} onClick={() => setMode("pick")} testId="add-editor-tab-pick">
                            Pick from database
                        </TabBtn>
                        <TabBtn active={mode === "new"} onClick={() => setMode("new")} testId="add-editor-tab-new">
                            Add someone new
                        </TabBtn>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-auto">
                    {mode === "pick" ? (
                        <PickFromDatabasePanel
                            alreadyOn={alreadyOn}
                            onAddEditorIds={onAddEditorIds}
                            onClose={onClose}
                        />
                    ) : (
                        <AddNewEditorPanel
                            onCreateEditor={onCreateEditor}
                            onClose={onClose}
                            onSwitchToPick={() => setMode("pick")}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function TabBtn({ active, onClick, testId, children }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={
                "px-4 py-2 text-sm font-mono border-b-2 transition-colors " +
                (active
                    ? "border-[#8C2D19] text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground")
            }
        >
            {children}
        </button>
    );
}

function PickFromDatabasePanel({ alreadyOn, onAddEditorIds, onClose }) {
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [editors, setEditors] = useState([]);
    const [facets, setFacets] = useState({ categories: [] });
    const [loading, setLoading] = useState(true);
    const [checked, setChecked] = useState({});
    const [adding, setAdding] = useState(false);

    // Fetch on mount + when query/category changes (debounced)
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const params = new URLSearchParams();
                if (q.trim()) params.set("q", q.trim());
                if (category) params.set("category", category);
                params.set("limit", "500");
                const [ed, fc] = await Promise.all([
                    api.get(`/editors?${params.toString()}`).then((r) => r.data),
                    facets.categories.length
                        ? Promise.resolve({ data: facets })
                        : api.get(`/editors/facets`).then((r) => r.data),
                ]);
                if (cancelled) return;
                setEditors(ed);
                if (fc && !facets.categories.length) setFacets(fc);
            } catch (e) {
                if (!cancelled) toast.error("Failed to load editors");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 200);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, category]);

    const checkedCount = Object.values(checked).filter(Boolean).length;
    const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

    const submit = async () => {
        const ids = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
        if (!ids.length) return toast.error("Select at least one editor");
        setAdding(true);
        try {
            await onAddEditorIds(ids);
            toast.success(`${ids.length} editor(s) added`);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add");
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="p-6 flex flex-col gap-4" data-testid="pick-editor-panel">
            <div className="flex gap-2 flex-wrap items-center">
                <input
                    className="input-flat flex-1 min-w-[240px]"
                    placeholder="Search name, house, imprint, email…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    autoFocus
                    data-testid="pick-editor-search"
                />
                <select
                    className="input-flat w-auto"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="pick-editor-category"
                >
                    <option value="">All categories</option>
                    {(facets.categories || []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>

            <div className="border border-border rounded-sm bg-white/60 divide-y divide-border overflow-auto" style={{ maxHeight: "48vh" }} data-testid="pick-editor-list">
                {loading ? (
                    <div className="p-6 text-sm text-muted-foreground pulse-mono font-mono">Loading editors…</div>
                ) : editors.length === 0 ? (
                    <div className="p-6 text-sm text-muted-foreground">No editors match.</div>
                ) : (
                    editors.map((ed) => {
                        const on = alreadyOn.has(ed.id);
                        return (
                            <label
                                key={ed.id}
                                htmlFor={`pick-${ed.id}`}
                                className={"flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 cursor-pointer " + (on ? "opacity-60" : "")}
                                data-testid={`pick-editor-row-${ed.id}`}
                            >
                                <input
                                    id={`pick-${ed.id}`}
                                    type="checkbox"
                                    className="shrink-0"
                                    disabled={on}
                                    checked={!!checked[ed.id]}
                                    onChange={() => toggle(ed.id)}
                                    data-testid={`pick-editor-cb-${ed.id}`}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="font-serif text-lg leading-tight truncate">
                                        {ed.name}{" "}
                                        {on && <Chip>Already on list</Chip>}
                                    </div>
                                    <div className="label-mono truncate">
                                        {[ed.title, ed.imprint, ed.house].filter(Boolean).join(" · ") || "—"}
                                    </div>
                                    <div className="text-xs font-mono text-muted-foreground truncate">
                                        {ed.email || "— no email —"}
                                    </div>
                                </div>
                                {(ed.categories || []).slice(0, 3).map((c) => (
                                    <Chip key={c}>{c}</Chip>
                                ))}
                            </label>
                        );
                    })
                )}
            </div>

            <div className="flex justify-between items-center gap-2">
                <div className="label-mono">
                    {checkedCount} selected · {editors.length} shown
                </div>
                <div className="flex gap-2">
                    <button className="btn-ghost" onClick={onClose} data-testid="pick-editor-cancel">Cancel</button>
                    <button
                        className="btn-primary disabled:opacity-60"
                        onClick={submit}
                        disabled={!checkedCount || adding}
                        data-testid="pick-editor-add-btn"
                    >
                        {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                        Add {checkedCount || ""} to submission list
                    </button>
                </div>
            </div>
        </div>
    );
}

function AddNewEditorPanel({ onCreateEditor, onClose, onSwitchToPick }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        house: "",
        imprint: "",
        title: "",
        pronouns: "",
        preferences_sub: "",
        notes: "",
    });
    const [duplicate, setDuplicate] = useState(null); // matched existing editor (if any)
    const [saving, setSaving] = useState(false);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Look up an existing editor by email whenever the user pauses typing.
    useEffect(() => {
        const email = form.email.trim().toLowerCase();
        if (!email || !email.includes("@")) {
            setDuplicate(null);
            return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const rows = await api.get(`/editors?q=${encodeURIComponent(email)}&limit=20`).then((r) => r.data);
                if (cancelled) return;
                const match = rows.find((r) => (r.email || "").toLowerCase() === email);
                setDuplicate(match || null);
            } catch {
                if (!cancelled) setDuplicate(null);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form.email]);

    const submit = async () => {
        const name = form.name.trim();
        const email = form.email.trim();
        if (!name) return toast.error("Name is required");
        if (!email || !email.includes("@")) return toast.error("A valid email is required");
        setSaving(true);
        try {
            const created = await onCreateEditor({
                name,
                email,
                house: form.house.trim(),
                imprint: form.imprint.trim(),
                title: form.title.trim(),
                pronouns: form.pronouns.trim(),
                preferences_sub: form.preferences_sub.trim(),
                notes: form.notes.trim(),
                status: "active",
            });
            toast.success(`${created.name} added to your database and submission list`);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add editor");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="new-editor-panel">
            <p className="text-sm text-muted-foreground">
                New editors are saved to your shared database (they&apos;ll show up in future AI suggestions
                across projects) and added to <em>this</em> project&apos;s submission list.
            </p>

            {duplicate && (
                <div
                    className="border border-[#8C2D19]/40 bg-[#8C2D19]/[0.06] rounded-sm p-4 flex items-start gap-3"
                    data-testid="new-editor-duplicate-warning"
                >
                    <div className="grow">
                        <div className="label-mono text-[#8C2D19] mb-1">Already in your database</div>
                        <div className="text-sm">
                            <span className="font-serif text-base">{duplicate.name}</span>
                            {" · "}
                            <span className="text-muted-foreground">
                                {[duplicate.title, duplicate.imprint, duplicate.house].filter(Boolean).join(" · ") || "—"}
                            </span>
                        </div>
                    </div>
                    <button
                        className="btn-primary text-xs shrink-0"
                        onClick={onSwitchToPick}
                        data-testid="new-editor-switch-to-pick"
                    >
                        Pick them instead
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Name *" value={form.name} onChange={(v) => setField("name", v)} testId="new-editor-name" autoFocus />
                <TextField label="Email *" value={form.email} onChange={(v) => setField("email", v)} testId="new-editor-email" placeholder="editor@publisher.com" mono />
                <TextField label="House" value={form.house} onChange={(v) => setField("house", v)} testId="new-editor-house" />
                <TextField label="Imprint" value={form.imprint} onChange={(v) => setField("imprint", v)} testId="new-editor-imprint" />
                <TextField label="Title" value={form.title} onChange={(v) => setField("title", v)} testId="new-editor-title" placeholder="Senior Editor, etc." />
                <TextField label="Pronouns" value={form.pronouns} onChange={(v) => setField("pronouns", v)} testId="new-editor-pronouns" placeholder="she/her, they/them…" />
            </div>
            <TextField
                label="Preferences (categories)"
                value={form.preferences_sub}
                onChange={(v) => setField("preferences_sub", v)}
                testId="new-editor-prefs"
                placeholder="PB, MG, YA, Graphic Novel…"
                helper="Comma-separated. Used for auto-categorization and AI matching."
            />
            <label className="block">
                <span className="label-mono">Notes</span>
                <textarea
                    className="input-flat mt-2 min-h-[80px]"
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    placeholder="MSWL notes, comps they've mentioned, meetings, etc."
                    data-testid="new-editor-notes"
                />
            </label>

            <div className="flex justify-end gap-2 pt-2">
                <button className="btn-ghost" onClick={onClose} data-testid="new-editor-cancel">Cancel</button>
                <button
                    className="btn-primary disabled:opacity-60"
                    onClick={submit}
                    disabled={saving}
                    data-testid="new-editor-submit"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                    Add to database & list
                </button>
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, testId, placeholder, mono, autoFocus, helper }) {
    return (
        <label className="block">
            <span className="label-mono">{label}</span>
            <input
                className={"input-flat mt-2 " + (mono ? "font-mono" : "")}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                data-testid={testId}
            />
            {helper && <div className="mt-1 text-xs text-muted-foreground">{helper}</div>}
        </label>
    );
}
